import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { clientApi } from "../../api";
import { clearAppCache } from "../../utils/clearCache";
import { getToken, clearSession } from "../../utils/authStorage";

/**
 * Shell for the /client/* sub-routes. Replaces the single-page
 * ClientDashboard and owns the sidebar + topbar chrome.
 */
export default function ClientLayout({ children }) {
  const { user, logout, login } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [venue, setVenue] = useState(null);

  const clientRole = user?.clientRole || "admin";
  const isAdmin   = clientRole === "admin";
  // Gate provider-specific nav on the venue actually being loaded. Until
  // /me resolves, `venue` is null — defaulting to S7000 there made S7000
  // tools (PIN Lookup, Reset PIN, Live Floor…) flash into the sidebar on
  // every page load for IGT venues before disappearing. Show neither
  // provider's tools until we know which provider this venue is.
  const isIgt     = (venue?.provider_type === "igt");
  const isS7000   = (!!venue && (venue.provider_type === "s7000" || !venue.provider_type));
  const isCircleScan = (venue?.provider_type === "circlescan");
  // Portal feature flag — digital membership (Litecard). Default ON; when the
  // venue turns it off we hide the Digital Passes + Digital Card menus.
  const digitalPassesOn = venue?.digital_passes_enabled !== false;
  // Admin-configured per-venue menu visibility. Keys live in
  // config/clientMenus.js (kept in sync with the vis(...) calls below); a key
  // present in hidden_menus removes that item from the sidebar. Layered on top
  // of the existing provider/role gating — never overrides it.
  const hiddenMenus = new Set(venue?.hidden_menus || []);
  const vis = (key) => !hiddenMenus.has(key);

  // Refresh identity from /me once on mount so stale localStorage (from pre-
  // role-split logins) gets upgraded to the new shape without forcing a
  // re-login.
  useEffect(() => {
    let alive = true;
    clientApi.getMe()
      .then((me) => {
        if (!alive) return;
        setVenue(me.venue || null);
        if (
          me.clientRole && me.clientRole !== user?.clientRole ||
          (me.venue && user?.venueName !== me.venue.name)
        ) {
          const token = getToken("client");
          login({ ...user, clientRole: me.clientRole, venueName: me.venue?.name }, token);
        }
      })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/client/login");
  };

  const venueName = venue?.name || user?.venueName || "Venue";
  const brandInitials = (venueName || "V").slice(0, 2).toUpperCase();

  // Admin "View As" impersonation — show a persistent banner + exit so it's
  // always obvious this isn't a real client session and changes are live.
  const impersonating = !!user?.impersonated;
  const exitViewAs = () => {
    clearSession("client");
    window.close(); // opened via window.open from the admin portal
    setTimeout(() => { window.location.href = "/admin/venues"; }, 150);
  };

  return (
    <div className="app-layout" style={impersonating ? { paddingTop: 34 } : undefined}>
      {impersonating && (
        <div className="impersonation-banner">
          <span>👁 Admin <strong>View As</strong> — you're in <strong>{venueName}</strong>'s client portal. Actions here are real.</span>
          <button type="button" className="impersonation-banner-exit" onClick={exitViewAs}>
            Exit View As
          </button>
        </div>
      )}
      <aside className={`sidebar ${collapsed ? "sidebar-collapsed" : ""}`}>
        <div className="sidebar-brand">
          {venue?.logo_url ? (
            <img src={venue.logo_url} alt={venueName} style={{ width: 36, height: 36, borderRadius: 8, objectFit: "cover" }} />
          ) : (
            <div className="brand-icon">{brandInitials}</div>
          )}
          <span className="brand-text">{venueName}</span>
        </div>

        <nav className="sidebar-nav">
          <NavItem to="/client" end label="Dashboard" icon={<IconDashboard />} />
          <NavItem to="/client/members" label="Members" icon={<IconMembers />} />
          {/* Provider-specific tools — each provider owns its own
              namespace under /client/<provider>/*. Nav items here are
              gated by venue.provider_type; an S7000 venue never sees
              IGT tools and vice versa. IGT tools are also admin-only
              (backend router mirrors this with requireClientRole). */}
          {isS7000 && vis("s7000.pin-lookup") && <NavItem to="/client/s7000/pin-lookup" label="PIN Lookup" icon={<IconLock />} />}
          {isS7000 && vis("s7000.reset-pin") && <NavItem to="/client/s7000/reset-pin" label="Reset PIN" icon={<IconKey />} />}
          {isIgt && isAdmin && vis("igt.find-member")    && <NavItem to="/client/igt/find-member"    label="Find Member"    icon={<IconSearch />} />}
          {isIgt && isAdmin && vis("igt.verify-pin")     && <NavItem to="/client/igt/verify-pin"     label="Verify PIN"     icon={<IconLock />} />}
          {isIgt && isAdmin && vis("igt.change-pin")     && <NavItem to="/client/igt/change-pin"     label="Change PIN"     icon={<IconKey />} />}
          {isIgt && isAdmin && vis("igt.update-profile") && <NavItem to="/client/igt/update-profile" label="Update Profile" icon={<IconEdit />} />}
          {digitalPassesOn && vis("passes") && <NavItem to="/client/passes" label="Digital Passes" icon={<IconPass />} />}
          {isS7000 && vis("s7000.live") && <NavItem to="/client/s7000/live" label="Live Floor" icon={<IconRadio />} />}
          {isS7000 && isAdmin && vis("s7000.bulk-points") && <NavItem to="/client/s7000/points/bulk" label="Bulk Points" icon={<IconGift />} />}
          <div className="nav-section-label">
            Reports
          </div>
          {vis("reports.expiring") && <NavItem to="/client/reports/expiring" label="Memberships Expiring" icon={<IconWarn />} />}
          {/* Cards Expiring reads S7000/IGT physical-card expiry (V_PUB_CARDS);
              CircleScan has no card table (passes are Litecard-driven), so gate
              it out per provider-isolation rather than show an empty report. */}
          {!isCircleScan && vis("reports.cards-expiring") && <NavItem to="/client/reports/cards-expiring" label="Cards Expiring" icon={<IconPass />} />}
          {/* Acquisition is provider-agnostic — it reads the portal's own signup
              records (members table), so it shows for every provider. Admin-only
              like the other analytical reports. */}
          {isAdmin && vis("reports.acquisition") && <NavItem to="/client/reports/acquisition" label="Acquisition" icon={<IconChart />} />}
          {isS7000 && isAdmin && vis("s7000.data-quality")   && <NavItem to="/client/s7000/reports/data-quality"       label="Data Quality"   icon={<IconAlert />} />}
          {isS7000 && isAdmin && vis("s7000.tag-coverage")   && <NavItem to="/client/s7000/reports/analysis-coverage"  label="Tag Coverage"   icon={<IconChart />} />}
          {isS7000 && isAdmin && vis("s7000.demographics")   && <NavItem to="/client/s7000/reports/demographics"       label="Demographics"   icon={<IconUsers />} />}
          {isS7000 && isAdmin && vis("s7000.sync-awareness") && <NavItem to="/client/s7000/reports/sync-awareness"     label="Sync Awareness" icon={<IconSync />} />}
          {isS7000 && isAdmin && vis("s7000.duplicates")     && <NavItem to="/client/s7000/duplicates"                 label="Duplicates"     icon={<IconMerge />} />}
          {vis("logs") && <NavItem to="/client/logs" label="Activity Logs" icon={<IconBolt />} />}
          {vis("products") && <NavItem to="/client/products" label="Products" icon={<IconBox />} />}
          <div className="nav-section-label" style={{ marginTop: "0.25rem" }}>
            Settings
          </div>
          <NavItem to="/client/settings/profile" label="My Profile" icon={<IconUser />} />
          {isAdmin && vis("settings.portal") && <NavItem to="/client/settings/portal" label="Portal" icon={<IconSliders />} />}
          {isAdmin && vis("settings.branding") && <NavItem to="/client/settings/branding" label="Branding" icon={<IconPalette />} />}
          {isAdmin && vis("settings.emails") && <NavItem to="/client/settings/emails" label="Emails" icon={<IconMail />} />}
          {/* Analysis Codes is an S7000-only classification concept; the page
              itself is "Not available" for other providers, so keep it out of
              a CircleScan/IGT sidebar. */}
          {isS7000 && isAdmin && vis("settings.analysis-codes") && <NavItem to="/client/settings/analysis-codes" label="Analysis Codes" icon={<IconTag />} />}
          {isAdmin && digitalPassesOn && vis("settings.digital-card") && <NavItem to="/client/settings/digital-card" label="Digital Card" icon={<IconPass />} />}
          {isAdmin && vis("settings.terms") && <NavItem to="/client/settings/terms" label="T&C" icon={<IconDoc />} />}
          {isAdmin && vis("settings.communications") && <NavItem to="/client/settings/communications" label="Comms" icon={<IconMail />} />}
          {isAdmin && vis("settings.staff") && <NavItem to="/client/settings/staff" label="Staff" icon={<IconUsers />} />}
        </nav>

        <div className="sidebar-footer">
          <button className="theme-toggle-btn" onClick={toggle} title={theme === "dark" ? "Light mode" : "Dark mode"}>
            {theme === "dark" ? <IconSun /> : <IconMoon />}
            <span>{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
          </button>
          <button className="theme-toggle-btn" onClick={() => clearAppCache()} title="Reload the latest version (clears cached assets; you stay signed in)">
            <IconRefresh />
            <span>Clear Cache</span>
          </button>
          <div className="sidebar-footer-info">
            <div className="avatar">{user?.name?.[0] || user?.email?.[0] || "U"}</div>
            <div style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>{user?.name || user?.email}</span>
              <span style={{ fontSize: "0.72rem", color: "var(--text-muted)", textTransform: "capitalize" }}>{clientRole}</span>
            </div>
          </div>
          <button className="sidebar-toggle-btn" onClick={() => setCollapsed(!collapsed)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16" style={{ transform: collapsed ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
              <polyline points="11 17 6 12 11 7" /><polyline points="18 17 13 12 18 7" />
            </svg>
          </button>
        </div>
      </aside>

      <div className={`main-content ${collapsed ? "collapsed" : ""}`}>
        <header className="topbar">
          <div className="topbar-user">
            <span>{user?.name || user?.email}</span>
            <button className="btn-icon" onClick={handleLogout} title="Sign out">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
            </button>
          </div>
        </header>
        <div className="content-area">{children}</div>
      </div>
    </div>
  );
}

function NavItem({ to, end, label, icon }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
      {icon}
      <span className="nav-label">{label}</span>
    </NavLink>
  );
}

/* ── Icons (inline to avoid extra deps) ─────────────────────── */
const IconDashboard = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);
const IconMembers = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
  </svg>
);
const IconPass = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="6" width="20" height="12" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
);
const IconWarn = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
);
const IconBolt = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
);
const IconBox = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>
);
const IconUser = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
);
const IconUsers = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
);
const IconPalette = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="13.5" cy="6.5" r=".5"/><circle cx="17.5" cy="10.5" r=".5"/><circle cx="8.5" cy="7.5" r=".5"/><circle cx="6.5" cy="12.5" r=".5"/><path d="M12 2A10 10 0 102 12a2 2 0 002 2h3a2 2 0 002 2v1a2 2 0 004 0v-1a2 2 0 012-2h3a2 2 0 002-2 10 10 0 00-10-10z"/></svg>
);
const IconMail = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
);
const IconTag = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41L11 3.83a2 2 0 00-1.41-.58H4a2 2 0 00-2 2v5.59a2 2 0 00.58 1.41L12.17 22a2 2 0 002.83 0l5.59-5.59a2 2 0 000-2.83z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
);
const IconDoc = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
);
const IconSun = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
);
const IconLock = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
);
const IconKey = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>
);
const IconChart = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
);
const IconRadio = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="2"/><path d="M16.24 7.76a6 6 0 010 8.49m-8.48-.01a6 6 0 010-8.49m11.31-2.82a10 10 0 010 14.14m-14.14 0a10 10 0 010-14.14"/></svg>
);
const IconAlert = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
);
const IconGift = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/></svg>
);
const IconSync = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
);
const IconMerge = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="18" r="3"/><circle cx="6" cy="6" r="3"/><path d="M6 21V9a9 9 0 009 9"/></svg>
);
const IconMoon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>
);
const IconRefresh = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
);
const IconSliders = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>
);
const IconSearch = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
);
const IconEdit = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
);
